import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { signInWithGoogle, signUpWithEmail, signInWithEmail } from '@/lib/firebase/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user?: any) => void;
  isDonorFlow?: boolean;
  skipDashboardRedirect?: boolean;
  creatorId?: string;
}

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  isDonorFlow = false,
  skipDashboardRedirect = false,
  creatorId
}: AuthModalProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUpView, setIsSignUpView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      handleAuthSuccess(user);
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in');
    }
  };

  const handleEmailSignUp = async () => {
    if (!firstName || !lastName || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setAuthMessage(null);
    try {
      const user = await signUpWithEmail({
        firstName,
        lastName,
        email,
        password,
        creatorId
      });
      setAuthMessage(null);
      handleAuthSuccess(user);
    } catch (error: any) {
      let errorMessage = 'Failed to create account';
      const message = typeof error?.message === 'string' ? error.message : '';
      if (error?.code === 'auth/email-already-in-use' || message.toLowerCase().includes('already registered')) {
        errorMessage = 'Email already registered. Please log in instead.';
      } else if (message) {
        errorMessage = message;
      }

      setAuthMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async () => {
    if (!email || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    setIsLoading(true);
    setAuthMessage(null);
    try {
      const user = await signInWithEmail(email, password);
      setAuthMessage(null);
      handleAuthSuccess(user);
    } catch (error: any) {
      // Provide user-friendly error messages
      let errorMessage = 'Failed to sign in';
      if (error.code === 'auth/invalid-credential' || error.message?.includes('invalid-credential')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setAuthMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = async (user: any) => {
    onSuccess(user);
    onClose();
    if (!skipDashboardRedirect) {
      navigate('/dashboard');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[70]">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold">
              {isDonorFlow ? 'Sign up to become a supporter' : (isSignUpView ? 'Create your account' : 'Welcome back')}
            </Dialog.Title>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            <img
              src="/google.svg"
              alt="Google"
              className="w-5 h-5"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";
              }}
            />
            Continue with Google
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          <form className="space-y-4" onSubmit={(e) => {
            e.preventDefault();
            isSignUpView ? handleEmailSignUp() : handleEmailSignIn();
          }}>
            {isSignUpView && (
              <>
                <input
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </>
            )}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-white rounded-lg px-4 py-2 hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : (isSignUpView ? 'Sign up' : 'Sign in')}
            </button>
          </form>

          {authMessage && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>{authMessage}</span>
                {isSignUpView && (
                  <button
                    type="button"
                    onClick={() => setIsSignUpView(false)}
                    className="text-blue-700 font-medium hover:underline"
                  >
                    Login here
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="text-center text-sm">
            <span className="text-gray-600">
              {isSignUpView ? 'Already a supporter?' : "Don't have an account?"}
            </span>
            <button
              onClick={() => setIsSignUpView(!isSignUpView)}
              className="ml-1 text-primary hover:underline"
            >
              {isSignUpView ? 'Login' : 'Sign up'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}